/**
 * Kasuti patterns — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_stitch_charts`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const kasutiPatternsPack: ToilePack = {
    slug: 'kasuti-patterns',
    category: 'craft',
    name: 'Kasuti patterns',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'gopura', label: 'Gopura', subject: 'a kasuti chart of a temple gopura motif in gavanti double running stitch, a stepped tower of straight stitches over a faint counting grid, symmetrical about its centre'},
        {key: 'chariot', label: 'Chariot', subject: 'a kasuti chart of a temple chariot (ratha) motif with a pointed canopy and wheels, worked in straight and diagonal stitches on the grid'},
        {key: 'border', label: 'Blouse border', subject: 'a kasuti border chart for a blouse sleeve, a repeating band of small lotus and elephant motifs in murgi zigzag stitch between two straight lines'},
    ],
    styles: [
        {key: 'grid', label: 'Counted grid', style: 'thin black stitch lines on a fine counting grid, each stitch one or two squares long', palette: 'black ink only on pure white', framing: 'centered on the sheet with an even white margin all round'},
        {key: 'solid', label: 'Solid cells', style: 'each stitched cell filled solid black on a fine grid so the motif reads as a bold silhouette', palette: 'black ink only on pure white', framing: 'centered on the sheet with an even white margin all round'},
        {key: 'twotone', label: 'Two tone', style: 'stitch lines in red and black on a fine grid, the second thread colour marking the return pass', palette: 'red and black line on pure white', framing: 'centered on the sheet with an even white margin all round'},
    ],
    directions: ['peacock', 'lotus', 'saree'],
    directionHint: 'try: peacock',
    seo: {
        title: 'Kasuti patterns: gopura, temple chariot, blouse border',
        metaDescription: 'Nine kasuti charts from Karnataka: a gopura tower, a temple chariot and a blouse border, counted on a fine grid in three styles.',
        h1: 'Kasuti patterns: a gopura, a temple chariot and a blouse border',
        tagline: 'Counted-thread motifs in gavanti and murgi stitches, identical on both sides of the cloth.',
        intro: 'Kasuti is the counted-thread embroidery of Karnataka, traditionally stitched on Ilkal sarees and blouse pieces. These nine charts show a gopura tower, a temple chariot and a blouse border over a fine grid. Each comes as a counted grid, a solid-cell silhouette, or a two-tone chart. Type a different motif and the same layout carries it.',
        useCases: [
            'a blouse sleeve border',
            'a motif on a saree edge',
            'a counted sampler',
            'a small piece for a wall',
        ],
        faq: [
            {q: 'What is gavanti?', a: 'A double running stitch, one of the two kasuti stitches used in these charts. The other is murgi.'},
            {q: 'What does the second colour in the two-tone style show?', a: 'The second thread colour marks the return pass.'},
            {q: 'Is the back the same as the front?', a: 'Yes. Kasuti is worked to look the same on both sides of the cloth.'},
        ],
    },
}
